import type { Property, Integration, ActionGroup } from '@super/shared-types'
import {
  INTEGRATIONS,
  ActionGroupSelectionType,
  type ActionSubtype,
  getPushIntegrationDisplayName,
  getActionSubtypeVariants,
} from '@super/shared-types'

/**
 * Selected push integration with optional action subtype and parameters
 * Uses string | null for flexibility with form values, but runtime values are ActionSubtype enum values
 * Generic P allows type-safe parameters for specific integration types
 */
export interface SelectedPushIntegration<
  P extends Record<string, unknown> = Record<string, unknown>,
> {
  integrationId: string
  actionSubtype: ActionSubtype | string | null // null for integrations without subtypes (legacy)
  parameters?: P // For variant-specific data (e.g., { taskVariant: 'resident_request' })
}

/**
 * Extracts property IDs from selected dropdown items
 * Returns empty array if "All properties" is selected
 */
export function extractPropertyIds(
  selectedItems: Array<{ id: string }>,
): string[] {
  // Check if "All properties" is selected
  const hasAllProperties = selectedItems.some(
    item => item.id === 'all-properties',
  )

  if (hasAllProperties) {
    return [] // Empty array means all properties
  }

  return selectedItems
    .filter(item => item.id !== 'all-properties')
    .map(item => item.id)
}

/**
 * Extracts integration IDs from selected dropdown items
 */
export function extractIntegrationIds(
  selectedItems: Array<{ id: string }>,
): string[] {
  return selectedItems.map(item => item.id)
}

/**
 * Creates push integration action groups from selected push integrations
 */
export function createPushIntegrationActionGroups(
  pushIntegrations: SelectedPushIntegration[],
  availableIntegrations: Integration[],
): ActionGroup[] {
  return pushIntegrations.map(pushIntegration => {
    const integration = availableIntegrations.find(
      i => i.id === pushIntegration.integrationId,
    )
    const integrationType = integration?.type as keyof typeof INTEGRATIONS
    const actionSubtype = pushIntegration.actionSubtype as ActionSubtype | null

    // Get display name based on action subtype and variant
    let displayName = getPushIntegrationDisplayName(
      integrationType,
      actionSubtype,
    )

    // If there's a variant in parameters, look up its display name
    const taskVariant = pushIntegration.parameters?.taskVariant as
      | string
      | undefined
    if (taskVariant && actionSubtype) {
      const variants = getActionSubtypeVariants(integrationType, actionSubtype)
      const variant = variants?.find(v => v.id === taskVariant)
      if (variant) {
        displayName = `${INTEGRATIONS[integrationType]?.NAME} - ${variant.name}`
      }
    }

    return {
      selectionType: ActionGroupSelectionType.ALWAYS,
      selectionCriteria: null,
      name: displayName,
      actions: [
        {
          actionDefinition: {
            actionType: 'PUSH_INTEGRATION',
          },
          parameters: pushIntegration.parameters || {},
          integrationId: pushIntegration.integrationId,
          actionSubtype,
        },
      ],
    }
  })
}

/**
 * Extracts push integrations (with action subtypes and parameters) from action groups
 */
export function extractPushIntegrations(
  actionGroups: ActionGroup[],
): SelectedPushIntegration[] {
  return actionGroups
    .filter(ag => ag.selectionType === ActionGroupSelectionType.ALWAYS)
    .flatMap(ag => ag.actions || [])
    .filter(
      action => action.actionDefinition?.actionType === 'PUSH_INTEGRATION',
    )
    .map(action => ({
      integrationId: action.integrationId!,
      actionSubtype: action.actionSubtype || null,
      parameters: action.parameters || undefined,
    }))
    .filter(item => item.integrationId)
}

/**
 * Initializes selected property IDs from property IDs array
 * Returns the matching property IDs that exist in the available properties
 */
export function getValidPropertyIds(
  propertyIds: string[] | undefined,
  availableProperties: Property[],
): string[] {
  if (!propertyIds || propertyIds.length === 0) {
    return []
  }

  const availablePropertyIds = new Set(availableProperties.map(p => p.id))
  return propertyIds.filter(id => availablePropertyIds.has(id))
}

/**
 * Gets push integrations from action groups that are available
 */
export function getValidPushIntegrations(
  actionGroups: ActionGroup[] | undefined,
  availableIntegrations: Integration[],
): SelectedPushIntegration[] {
  if (!actionGroups || actionGroups.length === 0) {
    return []
  }

  const pushIntegrations = extractPushIntegrations(actionGroups)
  const availableIntegrationIds = new Set(availableIntegrations.map(i => i.id))

  return pushIntegrations.filter(item =>
    availableIntegrationIds.has(item.integrationId),
  )
}

/**
 * Filters action groups to only include conditional actions
 */
export function filterConditionalActions(
  actionGroups: ActionGroup[],
): ActionGroup[] {
  return actionGroups.filter(
    ag =>
      ag.selectionType === ActionGroupSelectionType.CONDITIONAL ||
      !ag.selectionType,
  )
}

/**
 * Combines conditional action groups with push integration action groups
 */
export function combineActionGroupsForVisualization(
  interpretedActions: ActionGroup[],
  pushIntegrations: SelectedPushIntegration[],
  availableIntegrations: Integration[],
): ActionGroup[] {
  // Keep only conditional actions from interpreted results (filter out push integrations)
  const conditionalActions = filterConditionalActions(interpretedActions)

  // Build push integration groups from current form selection
  const pushGroups = createPushIntegrationActionGroups(
    pushIntegrations,
    availableIntegrations,
  )

  return [...conditionalActions, ...pushGroups]
}

/**
 * Gets the integration name for display purposes
 */
export function getIntegrationDisplayName(integration: Integration): string {
  const integrationType = integration.type as keyof typeof INTEGRATIONS
  const integrationConfig = INTEGRATIONS[integrationType]
  return integrationConfig?.NAME || integration.type
}

/**
 * Gets the property display label
 */
export function getPropertyDisplayLabel(property: Property): string {
  return property.name || property.externalId
}

/**
 * Gets the property display sublabel (city, state)
 */
export function getPropertyDisplaySublabel(property: Property): string {
  return `${property.propertyAddress.addressCity}, ${property.propertyAddress.addressState}`
}

// Clear stale `when` on single-group rules so the converter writes ALWAYS.
export function normalizeActionGroupsForSave<G extends { when: string }>(
  groups: G[],
): G[] {
  if (groups.length !== 1) return groups
  return groups.map(group => ({ ...group, when: '' }))
}
